import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { HistoryMovement, Movement } from 'types';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import movementTypes from 'modules/transactions/constants/movementTypes';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassOption';

import { RenumerationModalContextType } from '../types/RenumerationModalContextType';
import { renumerationModalInitValue } from '../constants/renumerationModalInitValue';
import { useRenumerationRanges } from '../hooks/useRenumerationRanges';
import { getHistoryMovements } from '../utils/getHistoryMovements';
import { getMovements } from '../utils/getMovements';

const RenumerationModalContext = createContext<RenumerationModalContextType>(
  renumerationModalInitValue
);

export const RenumerationModalProvider: FC<{
  children: ReactNode;
  size: string;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const [scriptureView, setScriptureView] = useState<boolean>(true);

  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [isAutomaticRenumeration, setIsAutomaticRenumeration] =
    useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [isEntireClassRenumeration, setIsEntireClassRenumeration] =
    useState<boolean>(
      renumerationModalInitValue.data.isEntireClassRenumeration
    );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId: actualSociety?._id || '',
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptions({
      societyId: actualSociety?._id,
      includeShares: false,
      autoSelectFirst: false,
    });

  const {
    data: rangesData,
    actions: rangesActions,
    isLoading: isLoadingRanges,
  } = useRenumerationRanges({
    holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    partnerId: partnerOptionsData.selectedPartnerOption?.value,
    page,
    rowsPerPage,
    isEntireClassRenumeration,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeRenumerationMode = () => {
    partnerOptionsActions.handleChangePartnerOption(null);
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(null);

    rangesActions.handleResetValues();
    setIsAutomaticRenumeration((prev) => !prev);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPage(1);
    setRowsPerPage(newRowsPerPage);
  };

  const handleChangeIsEntireClassRenumeration = () => {
    rangesActions.handleResetValues();
    partnerOptionsActions.handleChangePartnerOption(null);
    setIsEntireClassRenumeration((prev) => !prev);
  };

  const handleChangeHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setIsEntireClassRenumeration(false);
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(selectedOption);
    rangesActions.handleResetValues();
  };

  const renumberEntireClass = () => {
    const newMovement: Movement = {
      shareFrom: 0,
      shareTo: 0,
      shareClass: holdingClassOptionsData.selectedHoldingClass || '',
      movementType: movementTypes.ENTIRE_CLASS_RENUMERATION,
    };

    const historyMovement: HistoryMovement = {
      from: 0,
      to: 0,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      movementType: movementTypes.ENTIRE_CLASS_RENUMERATION,
    };

    setMovements([newMovement]);
    setHistoryMovements([historyMovement]);
  };

  const renumberEntireSociety = () => {
    const newMovement: Movement = {
      shareFrom: 0,
      shareTo: 0,
      movementType: movementTypes.AUTOMATIC_RENUMERATION,
    };

    const historyMovement: HistoryMovement = {
      from: 0,
      to: 0,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      movementType: movementTypes.AUTOMATIC_RENUMERATION,
    };

    setMovements([newMovement]);
    setHistoryMovements([historyMovement]);
  };

  const handleSaveMovement = async () => {
    if (isEntireClassRenumeration) {
      renumberEntireClass();
      return;
    }

    if (isAutomaticRenumeration) {
      renumberEntireSociety();
      return;
    }

    const isSaved = await rangesActions.handleSaveRanges();

    if (!isSaved) return;

    const holdingClassName =
      holdingClassOptionsData.selectedHoldingClassOption?.label || '';

    const newMovements = getMovements({
      reassignedRanges: rangesData.reassignedRanges,
    });

    const newHistoryMovements = getHistoryMovements({
      reassignedRanges: rangesData.reassignedRanges,
      holdingClassName,
    });

    setMovements((prev) => [...prev, ...newMovements]);
    setHistoryMovements((prev) => [...prev, ...newHistoryMovements]);
  };

  const handleSaveRenumeration = async () => {
    if (scriptureView) {
      setScriptureView(false);
      return;
    }

    try {
      setIsLoading(true);

      let uploadedDocument = null;

      if (file) {
        const newDocument = {
          file,
          date: new Date(date),
          size: file.size,
          name: file.name,
          author: user['_id'],
          society: actualSociety['_id'],
          fileType: fileTypes[file.type as keyof typeof fileTypes],
          category: documentTypes.SCRIPTURES,
          description: t('DocumentLinkedToOperation'),
        };
        uploadedDocument = await store.dispatch(addDocument(newDocument));
      }

      const newOperationDate = getNewOperationDate(lastOperationDate, date);

      const renumerationData = {
        movements,
        renumeration: {
          sharesCount: { actual: actualSociety?.sharesCount?.actual || 0 },
          partnersCount: actualSociety?.partnersCount || {
            active: 0,
            total: 0,
          },
        },
        date: newOperationDate,
        nominalValue: actualSociety.nominalValue,
        society: actualSociety['_id'],
        user: user['_id'],
        documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
        comments: cleanText(comments),
        notarialRegistration: {
          notaryName,
          notaryCity,
          protocolNr: notaryProtocol,
        },
        operationType: operationTypes.RENUMERATION,
      };

      await store.dispatch(addOperation(renumerationData));

      setIsLoading(false);
      successSwal(t('RenumerationSuccessfullyCompleted'));
    } catch (error) {
      errorSwal(t('ErrorWhenPerformingRenumeration'));
    } finally {
      handleCloseModal();
    }
  };

  return (
    <RenumerationModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          lastOperationDate: formattedLastOperationDate,
          isAutomaticRenumeration,
          selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
          partnerOptions: partnerOptionsData.partnerOptions,
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          ranges: rangesData.ranges,
          reassignedRanges: rangesData.reassignedRanges,
          savedRanges: rangesData.savedRanges,
          page,
          rowsPerPage,
          totalRanges: rangesData.totalRanges,
          totalClasses: actualSociety?.holdingClassesCount?.total || 0,
          totalPartners: actualSociety?.partnersCount?.active || 0,
          totalShares: actualSociety?.sharesCount?.actual || 0,
          overlapCount: rangesData.overlapCount,
          overlaps: rangesData.overlaps,
          historyMovements,
          isEntireClassRenumeration,
          isLoadingRanges,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleChangeRenumerationMode,
          handleChangePage,
          handleChangeRowsPerPage,
          handleChangePartner: partnerOptionsActions.handleChangePartnerOption,
          handleSearchPartner: partnerOptionsActions.handleSearchPartner,
          handleChangeHoldingClass,
          handleSearchHoldingClass:
            holdingClassOptionsActions.handleSearchHoldingClass,
          handleEditRange: rangesActions.handleEditRange,
          handleChangeIsEntireClassRenumeration,
          handleSaveMovement,
          handleSaveRenumeration,
        },
      }}
    >
      {children}
    </RenumerationModalContext.Provider>
  );
};

export const useRenumerationModal = () => {
  const context = useContext(RenumerationModalContext);
  if (!context) {
    throw new Error(
      'useRenumeration must be used within a RenumerationModalProvider'
    );
  }
  return context;
};
